import { Link } from 'react-router-dom';
import { formatDistanceToNow } from '../../lib/utils';

interface Post {
  id: string;
  content?: string;
  bodyText?: string;
  authorId?: string;
  segmentId?: string;
  weatherData?: {
    temperature?: number;
    description?: string;
    icon?: string;
  };
  weather?: {
    temp?: number;
    condition?: string;
    icon?: string;
  };
  createdAt: string;
  author: {
    id: string;
    displayName: string;
    handle: string;
    avatarUrl?: string;
  };
  media: Array<{
    id: string;
    type: string;
    url: string;
    thumbnailUrl?: string;
  }>;
  segment?: {
    id: string;
    name: string;
    canalName?: string;
    waterway?: {
      id: string;
      name: string;
    };
  };
  _count?: {
    reactions: number;
    comments: number;
  };
  stats?: {
    reactions: number;
    comments: number;
    shares?: number;
  };
  userReaction?: string;
}

interface PostCardProps {
  post: Post;
  onReact?: (postId: string, type: string) => void;
  onDelete?: (postId: string) => void;
  compact?: boolean;
  showFullContent?: boolean;
}

export default function PostCard({ post, onReact, onDelete, compact = false, showFullContent = false }: PostCardProps) {
  const handleReact = () => {
    onReact?.(post.id, 'like');
  };
  
  const content = post.content || post.bodyText || '';
  const reactionCount = post._count?.reactions ?? post.stats?.reactions ?? 0;
  const commentCount = post._count?.comments ?? post.stats?.comments ?? 0;
  const waterwayName = post.segment?.waterway?.name || post.segment?.canalName || '';
  const temperature = post.weatherData?.temperature ?? post.weather?.temp;
  const weatherDesc = post.weatherData?.description ?? post.weather?.condition;
  
  return (
    <article className="card overflow-hidden">
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between">
          <Link to={`/profile/${post.author.handle}`} className="flex items-center gap-3 group">
            <div className="avatar">
              {post.author.avatarUrl ? (
                <img src={post.author.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                post.author.displayName.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white group-hover:text-primary-500 transition-colors">
                {post.author.displayName}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                @{post.author.handle} · {formatDistanceToNow(new Date(post.createdAt))}
              </p>
            </div>
          </Link>
          
          {onDelete && (
            <div className="relative">
              <button
                onClick={() => onDelete(post.id)}
                className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                title="Delete post"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
        
        {/* Location badge */}
        {post.segment && (
          <Link
            to={`/map?segment=${post.segment.id}`}
            className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
          >
            <LocationIcon className="w-4 h-4" />
            <span>{post.segment.name}</span>
            {waterwayName && (
              <>
                <span className="text-primary-400 dark:text-primary-500">·</span>
                <span className="text-primary-500 dark:text-primary-400">{waterwayName}</span>
              </>
            )}
          </Link>
        )}
      </div>
      
      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{content}</p>
      </div>
      
      {/* Media */}
      {post.media.length > 0 && (
        <Link to={`/post/${post.id}`} className="block">
          {post.media.length === 1 ? (
            <MediaItem media={post.media[0]} />
          ) : (
            <div className={`grid gap-0.5 ${post.media.length === 2 ? 'grid-cols-2' : 'grid-cols-2'}`}>
              {post.media.slice(0, 4).map((m, i) => (
                <div
                  key={m.id}
                  className={`relative ${
                    post.media.length === 3 && i === 0 ? 'row-span-2' : ''
                  }`}
                >
                  <MediaItem media={m} aspectRatio={post.media.length === 1 ? 'auto' : 'square'} />
                  {i === 3 && post.media.length > 4 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-2xl font-bold">+{post.media.length - 4}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Link>
      )}
      
      {/* Weather data */}
      {(temperature !== undefined || weatherDesc) && (
        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-700/50">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <WeatherIcon className="w-4 h-4" />
            <span>
              {temperature !== undefined && `${Math.round(temperature)}°C`}
              {weatherDesc && ` · ${weatherDesc}`}
            </span>
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center gap-4">
        <button
          onClick={handleReact}
          className={`flex items-center gap-1.5 text-sm transition-colors ${
            post.userReaction
              ? 'text-red-500'
              : 'text-slate-500 dark:text-slate-400 hover:text-red-500'
          }`}
        >
          <HeartIcon className="w-5 h-5" filled={!!post.userReaction} />
          <span>{reactionCount || ''}</span>
        </button>
        
        <Link
          to={`/post/${post.id}`}
          className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-primary-500 transition-colors"
        >
          <CommentIcon className="w-5 h-5" />
          <span>{commentCount || ''}</span>
        </Link>
        
        <button
          className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-primary-500 transition-colors ml-auto"
        >
          <ShareIcon className="w-5 h-5" />
        </button>
        
        <Link
          to={`/post/${post.id}/report`}
          className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-orange-500 transition-colors"
          title="Report post"
        >
          <FlagIcon className="w-5 h-5" />
        </Link>
      </div>
    </article>
  );
}

function MediaItem({ media, aspectRatio = 'auto' }: { media: Post['media'][0]; aspectRatio?: 'auto' | 'square' }) {
  const isVideo = media.type === 'video';
  
  return (
    <div className={`bg-slate-100 dark:bg-slate-800 ${aspectRatio === 'square' ? 'aspect-square' : ''}`}>
      {isVideo ? (
        <video
          src={media.url}
          poster={media.thumbnailUrl}
          className="w-full h-full object-cover"
          controls={aspectRatio === 'auto'}
          muted
          playsInline
        />
      ) : (
        <img
          src={media.url}
          alt=""
          className={`w-full ${aspectRatio === 'auto' ? 'max-h-[500px]' : 'h-full'} object-cover`}
          loading="lazy"
        />
      )}
    </div>
  );
}

// Icons
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function HeartIcon({ className, filled }: { className?: string; filled?: boolean }) {
  return filled ? (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ) : (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function CommentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  );
}

function FlagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function WeatherIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
  );
}
